import { GCInstant } from 'src/lib/gcinstant';
import {
  ImageScaleView,
  ImageScaleViewOpts,
  ImageView,
  View,
  ViewOpts,
} from '@play-co/timestep-core/ui';
import StateObserver from 'src/StateObserver';
import { ProgressBar } from 'src/game/components/shared/ProgressBar';
import { startVisitSequence } from 'src/game/visit/visit.sequence';
import i18n from 'src/lib/i18n/i18n';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import LangBitmapFontTextView, {
  LangBitmapOpts,
} from 'src/lib/ui/components/LangBitmapFontTextView';
import { FontName } from 'src/lib/ui/config';
import buttons from 'src/lib/ui/config/buttons';
import { withLoading } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';
import { ExtendedChatMessage } from 'src/replicant/team/team.asyncGetters';
import {
  ChatMessageType,
  energyRequestAmount,
} from 'src/replicant/team/team.ruleset';
import { assertNever } from 'src/replicant/utils';
import {
  openTeamChestPopup,
  openTeamCompetitionPopup,
  trackTeamInteraction,
} from '../team.sequence';

type BubbleStyling = {
  root: ViewOpts;
  bg: ImageScaleViewOpts;
  name: LangBitmapOpts;
  headerImage?: string;
  nameShadowOpacity: number;
};

const chatBubbleStyling: Record<ChatMessageType, BubbleStyling> = {
  helpRequest: {
    root: {
      height: 212,
    },
    bg: {},
    name: {
      color: 'white',
      font: 'Body' as FontName,
    },
    headerImage: 'assets/ui/match2/team/chat/panel_chatBubble_cap_help.png',
    nameShadowOpacity: 0.5,
  },
  energyRequest: {
    root: {
      height: 212,
    },
    bg: {},
    name: {
      color: 'white',
      font: 'Body' as FontName,
    },
    headerImage: 'assets/ui/match2/team/chat/panel_chatBubble_cap_request.png',
    nameShadowOpacity: 0.5,
  },
  text: {
    root: {
      height: 160,
    },
    bg: {},
    name: {
      color: '#CE3752',
      font: 'Body' as FontName,
    },
    headerImage: undefined,
    nameShadowOpacity: 0,
  },
  emote: {
    root: {
      height: 151,
    },
    bg: {
      width: 191,
    },
    name: {
      color: 'white',
      font: 'Title' as FontName,
      x: 160,
      y: 20,
    },
    headerImage: undefined,
    nameShadowOpacity: 0.5,
  },
  event: {
    root: {
      height: 212,
    },
    bg: {},
    name: {
      color: 'white',
      font: 'Body' as FontName,
    },
    headerImage: 'assets/ui/match2/team/chat/panel_chatBubble_cap_event.png',
    nameShadowOpacity: 0.5,
  },
  chestEvent: {
    root: {
      height: 212,
    },
    bg: {},
    name: {
      color: 'white',
      font: 'Body' as FontName,
    },
    headerImage: 'assets/ui/match2/team/chat/panel_chatBubble_cap_event.png',
    nameShadowOpacity: 0.5,
  },
};

export class ChatEntry extends View {
  private readonly bg: ImageScaleView;
  private readonly headerBG: ImageScaleView;
  private readonly name: LangBitmapFontTextShadowView;
  private readonly subTitle: LangBitmapFontTextShadowView;
  private readonly ctaButton: ButtonScaleViewWithText;
  private readonly content: View;

  constructor(opts: ViewOpts) {
    super({ width: 700, height: 212, ...opts });

    this.bg = new ImageScaleView({
      superview: this,
      width: this.style.width - 70,
      height: this.style.height,
      image: 'assets/ui/match2/team/chat/panel_chatbubble.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 40, right: 100 },
        vertical: { top: 100, bottom: 40 },
      },
      flipX: true,
    });

    this.content = new View({
      superview: this,
      width: this.style.width - 70,
      height: this.style.height,
    });

    this.headerBG = new ImageScaleView({
      superview: this,
      y: 7,
      width: this.bg.style.width - 43,
      height: 58,
      scaleMethod: '3slice',
      sourceSlices: { horizontal: { left: 40, right: 40 } },
    });

    this.name = new LangBitmapFontTextShadowView({
      superview: this.headerBG,
      font: 'Body',
      size: 26,
      color: 'white',
      width: this.headerBG.style.width / 2,
      height: 30,
      y: 13,
      align: 'left',
      verticalAlign: 'center',
      textShadowOpacity: 0.5,
    });

    this.subTitle = new LangBitmapFontTextShadowView({
      superview: this.headerBG,
      font: 'Body',
      size: 26,
      color: '#FCFF62',
      width: this.headerBG.style.width / 2,
      height: 30,
      x: this.headerBG.style.width / 2 - 30,
      y: 13,
      align: 'right',
      verticalAlign: 'center',
      textShadowOpacity: 0.5,
    });

    this.ctaButton = new ButtonScaleViewWithText({
      ...buttons.secondary,
      superview: this,
      fontSize: 42,
      labelOffsetY: -2,
      labelOffsetX: -6,
      font: 'Body',
      width: 165,
      height: 75,
      x: 510,
      y: 130,
      localeText: () => 'Help',
      onClick: async () => {
        sounds.sfx.play('ok');
      },
      centerOnOrigin: true,
      tracking: {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.TAB_CHAT_SEND_HELP,
      },
    });
  }

  public setup(message: ExtendedChatMessage) {
    this.name.updateOpts({ align: 'left', x: 30 });
    this.name.localeText = () => message.senderName || 'Player';
    this.content.removeAllSubviews();
    this.setupBaseStyle(message.contents.type as any);
    this.ctaButton.onClick = async () => {};
    this.ctaButton.setDisabled(false);

    const type = message.contents.type;
    const itsMe = message.senderId === GCInstant.playerID;
    switch (type) {
      case 'helpRequest':
        this.setupHelpRequest(!itsMe, message);
        break;
      case 'energyRequest':
        this.setupEnergyRequest(!itsMe, message);
        break;
      case 'text':
        this.setupText(message.contents.id ?? 'message1');
        break;
      case 'emote':
        this.setupEmote(message.contents.id ?? 'thumb');
        break;
      case 'event':
        this.setupEvent();
        break;
      case 'chestEvent':
        this.setupChestEvent();
        break;
      default:
        assertNever(type);
    }

    // The user may join the team in the event and thus be the sender,
    // but event messages are like system messages and should not be from someone.
    if (itsMe && type !== 'event' && type !== 'chestEvent') {
      this.setupMe(type);
    } else {
      this.setupOther();
    }
  }

  private setupBaseStyle(bubbleType: keyof typeof chatBubbleStyling) {
    const style = chatBubbleStyling[bubbleType];
    this.updateOpts(style.root);
    this.bg.updateOpts({
      width: this.style.width - 70,
      ...style.root,
      ...style.bg,
    });
    this.headerBG.setImage(style.headerImage);
    this.name.updateOpts(style.name);
    this.name.updateShadow({ textShadowOpacity: style.nameShadowOpacity });
  }

  private setupHelpRequest(helpVisible: boolean, message: ExtendedChatMessage) {
    this.ctaButton.localeText = () =>
      i18n('match2.team.chat.bubble.helpRequest.button');
    this.ctaButton.updateOpts({ visible: helpVisible });
    this.subTitle.localeText = () =>
      i18n('match2.team.chat.bubble.helpRequest.subtitle');
    this.subTitle.show();

    new ImageScaleView({
      superview: this.content,
      width: 100,
      height: 100,
      x: 106,
      y: this.content.style.height / 2 + 24,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      image: `assets/ui/species/linepet_${message.contents.id ?? 'ball'}.png`,
    });

    new LangBitmapFontTextView({
      superview: this.content,
      font: 'Body',
      size: 24,
      color: '#373658',
      localeText: () => i18n('match2.team.chat.bubble.helpRequest.message'),
      width: 300,
      height: 100,
      x: 160,
      y: 74,
      align: 'left',
      verticalAlign: 'center',
    });

    this.ctaButton.onClick = async () => {
      sounds.sfx.play('ok');

      if (
        await openPopupPromise('popupConfirmTeamVisit', {
          speciesId: message.contents.id ?? 'ball',
        })
      ) {
        this.emit('Close');

        await startVisitSequence({ friendId: message.senderId }, undefined);
      }
    };
  }

  private setupEnergyRequest(
    helpVisible: boolean,
    message: ExtendedChatMessage,
  ) {
    this.subTitle.localeText = () =>
      i18n('match2.team.chat.bubble.energyRequest.subtitle');
    this.subTitle.show();

    this.ctaButton.localeText = () =>
      i18n('match2.team.chat.bubble.energyRequest.button');
    this.ctaButton.updateOpts({ visible: helpVisible });

    new LangBitmapFontTextView({
      superview: this.content,
      font: 'Body',
      size: 24,
      color: '#373658',
      localeText: () => i18n('match2.team.chat.bubble.energyRequest.message'),
      width: 200,
      height: 40,
      x: 120,
      y: 84,
      align: 'center',
      verticalAlign: 'center',
    });

    const progressBar = new ProgressBar({
      superview: this.content,
      bgStyle: {
        x: 220,
        y: this.style.height - 60,
        width: 270,
        height: 52,
        centerOnOrigin: true,
        image: 'assets/ui/match2/team/chat/energy_progress_bg.png',
      },
      fillStyle: {
        x: 2,
        y: 2,
        height: 45,
        paddingRight: 4,
        image: 'assets/ui/match2/team/chat/energy_progress_fill.png',
      },
    });

    const currentAmount = message.contents.value ?? 0;
    const progressLabel = new LangBitmapFontTextView({
      superview: this.content,
      font: 'Title',
      size: 34,
      color: 'white',
      localeText: () => currentAmount + '/' + energyRequestAmount,
      width: 200,
      height: 40,
      x: 120,
      y: 130,
      align: 'center',
      verticalAlign: 'center',
    });

    new ImageView({
      image: 'assets/ui/shared/icons/energy.png',
      superview: this.content,
      width: 158,
      height: 188,
      scale: 0.5,
      x: 50,
      y: 84,
    });

    progressBar.domain = [0, energyRequestAmount];
    progressBar.progress = currentAmount;

    if (!message.canRespond) {
      this.ctaButton.setDisabled(true);
      return;
    }

    this.ctaButton.onClick = async () => {
      sounds.sfx.play('ok');
      this.ctaButton.setDisabled(true);

      await withLoading(
        () => StateObserver.invoke.sendEnergyToTeamMember(message.senderId),
        {
          feature: Feature.TEAM._,
          subFeature: Feature.TEAM.SEND_ENERGY,
        },
      ).then((result) => {
        // Energy was not sent, bail!
        if (!result) {
          return;
        }
        this.emit('EnergySent');

        // Track the interaction.
        trackTeamInteraction('sendEnergy');
      });
    };
  }

  private setupText(messageId: string) {
    this.ctaButton.hide();
    this.subTitle.hide();

    new LangBitmapFontTextView({
      superview: this.content,
      font: 'Body',
      size: 26,
      color: '#373658',
      localeText: () => i18n(`match2.team.chat.messages.${messageId}`),
      width: this.style.width - 160,
      height: this.style.height - 90,
      x: 60,
      y: 56,
      align: 'left',
      verticalAlign: 'center',
      wordWrap: true,
    });
  }

  private setupEmote(emoteId: string) {
    this.ctaButton.hide();
    this.subTitle.hide();

    new ImageScaleView({
      superview: this.content,
      width: 120,
      height: 120,
      x: 106,
      y: this.style.height / 2,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      image: `assets/ui/match2/team/chat/emote/${emoteId}.png`,
    });
  }

  private setupEvent() {
    this.name.localeText = () => i18n('match2.team.chat.bubble.event.name');
    this.ctaButton.localeText = () =>
      i18n('match2.team.chat.bubble.event.button');
    this.subTitle.localeText = () =>
      i18n('match2.team.chat.bubble.event.subtitle');
    this.subTitle.show();

    new ImageScaleView({
      superview: this.content,
      width: 100,
      height: 100,
      x: 106,
      y: this.content.style.height / 2 + 24,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      image: 'assets/ui/match2/team/chat/icon_event.png',
    });

    new LangBitmapFontTextView({
      superview: this.content,
      font: 'Body',
      size: 24,
      color: '#373658',
      localeText: () => i18n('match2.team.chat.bubble.event.message'),
      width: 300,
      height: 100,
      x: 160,
      y: 74,
      align: 'left',
      verticalAlign: 'center',
    });

    this.ctaButton.onClick = async () => {
      sounds.sfx.play('ok');

      openTeamCompetitionPopup(undefined);
    };
  }

  private setupChestEvent() {
    this.name.localeText = () =>
      i18n('match2.team.chat.bubble.chestEvent.name');
    this.ctaButton.localeText = () =>
      i18n('match2.team.chat.bubble.chestEvent.button');
    this.subTitle.localeText = () =>
      i18n('match2.team.chat.bubble.chestEvent.subtitle');
    this.subTitle.show();

    new ImageScaleView({
      superview: this.content,
      width: 100,
      height: 100,
      x: 106,
      y: this.content.style.height / 2 + 24,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      image: 'assets/ui/match2/team/chest/team_chest_icon.png',
    });

    new LangBitmapFontTextView({
      superview: this.content,
      font: 'Body',
      size: 24,
      color: '#373658',
      localeText: () => i18n('match2.team.chat.bubble.chestEvent.message'),
      width: 300,
      height: 100,
      x: 160,
      y: 74,
      align: 'left',
      verticalAlign: 'center',
    });

    this.ctaButton.updateOpts({
      x: 530,
    });

    this.ctaButton.onClick = async () => {
      sounds.sfx.play('ok');

      openTeamChestPopup(undefined);
    };
  }

  private setupMe(type: string) {
    this.name.localeText = () => 'You';
    const offset = this.style.width - this.bg.style.width;
    this.bg.updateOpts({
      x: offset,
      flipX: false,
    });

    this.headerBG.updateOpts({
      x: offset + 10,
    });

    this.content.updateOpts({
      x: offset - 22,
    });

    if (type === 'emote') {
      this.name.updateOpts({
        x: -310,
        align: 'right',
      });
    }

    if (type === 'text') {
      this.name.updateOpts({
        color: '#4D78E5',
      });
    }
  }

  private setupOther() {
    this.bg.updateOpts({
      x: 0,
      flipX: true,
    });

    this.headerBG.updateOpts({
      x: this.bg.style.x + 32,
    });

    this.content.updateOpts({
      x: 0,
    });
  }
}
